/**
 * The company's Performance access, as the Engagement backend computes it from the engagement plan
 * and the Performance PRO add-on together. The module never compares plan numbers: these two keys
 * are the whole answer, so a plan added later needs no change here.
 */
export interface PerformanceAccessKeys {
    /** The engagement plan includes the free tier: Pro, or an active Enterprise plan. */
    isPerformanceFreeTierAllowed?: boolean;
    /** A Performance PRO subscription is active now. */
    isPerformanceProTierAllowed?: boolean;
}

export const PerformanceTier = {
    /** No Performance at all: a Freemium or Essential company without Performance PRO. */
    None: 'none',
    /** Self-Assessment and 360 Feedback only. */
    Free: 'free',
    /** The whole module. */
    Pro: 'pro',
} as const;

export type PerformanceTierValue = (typeof PerformanceTier)[keyof typeof PerformanceTier];

/** What an access point to a Performance PRO feature does for the reader. */
export const ProGateOutcome = {
    Show: 'show',
    /** Stays in place with a lock, and offers the upgrade instead of the feature. */
    Lock: 'lock',
    Hide: 'hide',
} as const;

export type ProGateOutcomeValue = (typeof ProGateOutcome)[keyof typeof ProGateOutcome];

/**
 * Reads the tier from the access keys.
 *
 * A host that sends neither key -- one deployed before them, or the module running on its own --
 * gets the whole module, as it did before the tiers existed: hiding everything from a paying company
 * on a missing field is the worse failure, and the server enforces the tier anyway.
 */
export const getPerformanceTier = ({
    isPerformanceFreeTierAllowed,
    isPerformanceProTierAllowed,
}: PerformanceAccessKeys): PerformanceTierValue => {
    const isUnknown =
        isPerformanceFreeTierAllowed === undefined && isPerformanceProTierAllowed === undefined;

    if (isUnknown || isPerformanceProTierAllowed) {
        return PerformanceTier.Pro;
    }

    return isPerformanceFreeTierAllowed ? PerformanceTier.Free : PerformanceTier.None;
};

export interface ProGateInput {
    tier: PerformanceTierValue;
    /** The reader holds the Performance permission the feature needs (or it needs none). */
    hasFeaturePermission: boolean;
    isAdmin: boolean;
    canManageAllDepartments: boolean;
}

/**
 * Entitlement and permission are separate axes, and both must allow a feature. A reader without the
 * feature's permission never sees it, whatever the company bought. Short of PRO, only administrators
 * and readers who manage every department's performance -- the people who could buy it -- see the
 * feature locked; everyone else does not see it at all.
 */
export const resolveProGate = ({
    tier,
    hasFeaturePermission,
    isAdmin,
    canManageAllDepartments,
}: ProGateInput): ProGateOutcomeValue => {
    if (!hasFeaturePermission) {
        return ProGateOutcome.Hide;
    }

    if (tier === PerformanceTier.Pro) {
        return ProGateOutcome.Show;
    }

    return isAdmin || canManageAllDepartments ? ProGateOutcome.Lock : ProGateOutcome.Hide;
};
